import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import type { LocalUpdateStatusDto } from '@his/shared';
import { describeError } from '@/lib/auth';
import { post } from '@/lib/api';
import { fetchUpdateStatus, useApplyUpdateMutation } from '@/lib/updates-api';
import { UPDATE_CANCEL_LAST_STEP, type LocalUpdateProgressDto } from '@his/shared';
import { Dialog } from '@/components/ui/layout';
import { Button } from '@/components/ui/primitives';
import { ReleaseNotes } from '@/components/ui/release-notes';
import { useToast } from '@/components/ui/toast';

const POLL_MS = 5_000;
const TIMEOUT_MS = 10 * 60_000;
const RELOAD_DELAY_MS = 1_500;

type Phase = 'confirm' | 'starting' | 'applying' | 'done' | 'failed';

export interface UpdateApplyDialogProps {
  status: LocalUpdateStatusDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function elapsedLabel(startedAt: number, now: number): string {
  const seconds = Math.max(0, Math.floor((now - startedAt) / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * "Update now" (plan §3.3): confirm, ask the server to start (202), then poll
 * `/updates/status` until a different version answers — the updater stops
 * the API for about a minute, so "server unreachable" is expected mid-way —
 * and reload. A `lastUpdateFailed` verdict or ten silent minutes ends in the
 * failed state with whatever log the server has.
 */
export function UpdateApplyDialog({ status, open, onOpenChange }: UpdateApplyDialogProps) {
  const toast = useToast();
  const pending = status.pendingUpdate;
  const [phase, setPhase] = useState<Phase>('confirm');
  const [failure, setFailure] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const [progress, setProgress] = useState<LocalUpdateProgressDto | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const fromVersion = useRef(status.runningVersion);
  const apply = useApplyUpdateMutation();

  useEffect(() => {
    if (!open) {
      setPhase('confirm');
      setFailure(null);
      setOffline(false);
      setStartedAt(null);
    }
  }, [open]);

  useEffect(() => {
    if (phase !== 'applying' || startedAt === null) return;
    let cancelled = false;
    const tick = (): void => {
      setNow(Date.now());
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (Date.now() - startedAt > TIMEOUT_MS) {
        setFailure('The update did not finish within 10 minutes. Check the server: C:\\HIS\\last-update.json and logs\\update-*.log.');
        setPhase('failed');
        return;
      }
      fetchUpdateStatus()
        .then((fresh) => {
          if (cancelled) return;
          setOffline(false);
          setProgress(fresh.progress ?? null);
          if (fresh.runningVersion !== fromVersion.current) {
            setPhase('done');
            toast.success(`Updated to version ${fresh.runningVersion}`, 'Reloading…');
            window.setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
            return;
          }
          if (fresh.lastUpdateFailed) {
            setFailure(fresh.lastUpdateLog ?? 'The updater reported a failure and rolled back.');
            setPhase('failed');
          }
        })
        .catch(() => {
          if (!cancelled) setOffline(true);
        });
    };
    const timer = window.setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [phase, startedAt, toast]);

  if (!pending) return null;

  const confirm = (): void => {
    setPhase('starting');
    fromVersion.current = status.runningVersion;
    apply.mutate(
      { version: pending.version },
      {
        onSuccess: () => {
          setStartedAt(Date.now());
          setNow(Date.now());
          setPhase('applying');
        },
        onError: (err) => {
          setFailure(describeError(err) || 'Could not start the update.');
          setPhase('failed');
        },
      },
    );
  };

  const cancel = (): void => {
    setCancelling(true);
    post('/updates/cancel')
      .then(() => toast.success('Cancel requested', 'The updater stops before it changes anything.'))
      .catch((err: unknown) => toast.error('Could not cancel', describeError(err)))
      .finally(() => setCancelling(false));
  };

  const hostOnly = status.fromServerHost === false;
  const canCancel = phase === 'applying' && !hostOnly && (progress === null || progress.step <= UPDATE_CANCEL_LAST_STEP);
  const locked = phase === 'applying' || phase === 'starting' || phase === 'done';
  const title = phase === 'done' ? 'Update complete' : phase === 'failed' ? 'Update did not complete' : `Update to version ${pending.version}`;
  const description =
    phase === 'confirm'
      ? `This server runs ${status.runningVersion}. Applying the update downloads the signed package, backs up the database, installs the new version and restarts the server. Everyone signed in will lose their connection for about a minute — pick a quiet moment.`
      : phase === 'starting'
        ? 'Asking the server to start the update…'
        : phase === 'applying'
          ? 'The updater is running. This page checks the server every few seconds and reloads itself when the new version answers.'
          : phase === 'done'
            ? 'The new version is running. Reloading this page…'
            : 'The server is still on its previous version.';

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!locked) onOpenChange(false);
      }}
      title={title}
      description={description}
      footer={
        phase === 'confirm' ? (
          <>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Not now
            </Button>
            <Button onClick={confirm} disabled={hostOnly}>
              <RefreshCw className="h-4 w-4" /> Update and restart
            </Button>
          </>
        ) : phase === 'failed' ? (
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        ) : undefined
      }
    >
      {phase === 'confirm' && hostOnly && (
        <p className="mb-3 rounded-md border border-warning/30 bg-warning/10 p-3 text-sm text-warning">
          Updates start only from a browser on the server itself. From here, use Schedule update in Settings → About.
        </p>
      )}

      {phase === 'confirm' && (
        <div className="max-h-56 overflow-y-auto rounded-md border bg-muted/40 p-3">
          <ReleaseNotes notes={pending.notes} />
        </div>
      )}

      {phase === 'applying' && startedAt !== null && (
        <div className="flex items-center gap-3 rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
          <Loader2 className="h-4 w-4 flex-none animate-spin text-primary" aria-hidden />
          <span>
            {offline ? 'Server is restarting — waiting for it to come back' : progress ? `Step ${progress.step} of ${progress.total}: ${progress.label}` : 'Updating'} ·{' '}
            {elapsedLabel(startedAt, now)}
          </span>
          {canCancel && (
            <Button size="sm" variant="outline" className="ms-auto" loading={cancelling} onClick={cancel}>
              Cancel
            </Button>
          )}
        </div>
      )}

      {phase === 'done' && (
        <div className="flex items-center gap-3 rounded-md border border-success/30 bg-success/10 p-3 text-sm text-success">
          <CheckCircle2 className="h-4 w-4 flex-none" aria-hidden /> Reloading…
        </div>
      )}

      {phase === 'failed' && (
        <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
          <p className="flex items-center gap-2 text-sm font-medium text-destructive">
            <AlertTriangle className="h-4 w-4 flex-none" aria-hidden /> Update failed
          </p>
          <pre className="max-h-48 overflow-auto whitespace-pre-wrap font-mono text-[11px] leading-4 text-foreground">{failure}</pre>
        </div>
      )}
    </Dialog>
  );
}

export interface ReleaseNotesDialogProps {
  version: string;
  notes: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ReleaseNotesDialog({ version, notes, open, onOpenChange }: ReleaseNotesDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={() => onOpenChange(false)}
      title={`What's new in ${version}`}
      footer={
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Close
        </Button>
      }
    >
      <ReleaseNotes notes={notes} />
    </Dialog>
  );
}
